"use client";

import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useSession } from "next-auth/react";
import type { User } from "next-auth";
import { useContestSearch } from "@/hooks/contest/useContestSearch";
import { ContestList, STATUS, StatusKey } from "./list";
import { SortBy, SortOrder } from "./list/ContestSortSection";
import { LockFilter } from "@/restapi/contest";
import { UserRoleConst } from "@/restapi/user";

type PeriodPreset = "all" | "this_week" | "this_month" | "next_month";

const toYmd = (d: Date) => d.toISOString().slice(0, 10);

const buildPeriodRange = (preset: PeriodPreset) => {
  const today = new Date();

  switch (preset) {
    case "this_week": {
      const start = new Date(today);
      start.setDate(today.getDate() - today.getDay());
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return { startFrom: toYmd(start), startTo: toYmd(end) };
    }
    case "this_month": {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { startFrom: toYmd(start), startTo: toYmd(end) };
    }
    case "next_month": {
      const start = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth() + 2, 0);
      return { startFrom: toYmd(start), startTo: toYmd(end) };
    }
    default:
      return {};
  }
};

type Props = {
  /** 公開ページでも管理者操作UIを出したい場合のみ true */
  isAdmin?: boolean;
};

type ContestListUser = User & {
  id: string;
  role?: number | string;
  name?: string | null;
  email?: string | null;
  image?: string | null;
};

const PublishedContest = ({ isAdmin = false }: Props) => {
  const { data: session, status } = useSession();
  const sessionUser = session?.user;

  const actorUuid = useMemo(() => (sessionUser?.id ?? "").trim(), [sessionUser?.id]);

  /**
   * 公開画面は非ログインでも取得する
   */
  const enabled = true;

  /**
   * viewer は管理者ログイン時のみ admin、それ以外は user
   */
  const viewer = sessionUser?.role === UserRoleConst.ADMIN ? ("admin" as const) : ("user" as const);

  /* --------------------
   * 検索
   * ------------------ */
  const [q, setQ] = useState("");
  const [qCommitted, setQCommitted] = useState("");

  /* --------------------
   * フィルタ
   * ------------------ */
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<StatusKey[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  /**
   * 公開側では lock UI は出さない前提だが、
   * ContestList の props 整合のため state は維持
   */
  const [lockFilter, setLockFilter] = useState<LockFilter>("all");

  /* --------------------
   * ソート
   * ------------------ */
  const [sortBy, setSortBy] = useState<SortBy>("group_name");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");

  /* --------------------
   * 期間
   * ------------------ */
  const [periodPreset, setPeriodPreset] = useState<PeriodPreset>("all");
  const [startFrom, setStartFrom] = useState<string | null>(null);
  const [startTo, setStartTo] = useState<string | null>(null);

  const isInvalidRange = useMemo(() => {
    if (!startFrom || !startTo) return false;
    return new Date(startFrom) > new Date(startTo);
  }, [startFrom, startTo]);

  const commitSearch = useCallback(() => {
    if (isInvalidRange) return;
    setQCommitted(q.trim());
  }, [q, isInvalidRange]);

  /**
   * 公開側の開催者フィルタ
   * 指定がある時だけ送る
   */
  const computedUserIds = useMemo(() => {
    return selectedUserIds.length > 0 ? selectedUserIds : [];
  }, [selectedUserIds]);

  /* --------------------
   * API パラメータ
   * ------------------ */
  const contestSearchParams = useMemo(() => {
    const statuses = selectedStatuses.slice().sort().map((k) => STATUS[k]);
    const presetRange = buildPeriodRange(periodPreset);

    const period =
      startFrom || startTo
        ? { startFrom: startFrom ?? undefined, startTo: startTo ?? undefined }
        : presetRange;

    return {
      enabled,
      screen: "public" as const,
      viewer,
      perPage: 10,
      isPublished: true,

      /**
       * 公開側はバックエンドで locked を除外する想定
       * 明示フィルタは送らない
       */
      isLocked: undefined,

      q: qCommitted || undefined,
      categoryIds: selectedCategoryIds,
      statuses,
      ...period,
      userIds: computedUserIds,
      defaultToSessionUserId: false,
      sortBy,
      sortOrder,
      actorUuid: actorUuid || undefined,
    };
  }, [
    enabled,
    viewer,
    qCommitted,
    selectedCategoryIds,
    selectedStatuses,
    periodPreset,
    startFrom,
    startTo,
    computedUserIds,
    sortBy,
    sortOrder,
    actorUuid,
  ]);

  const prevContestSearchParamsRef = useRef(contestSearchParams);
  const shouldResetScroll = useMemo(() => {
    const changed = prevContestSearchParamsRef.current !== contestSearchParams;
    if (changed) {
      prevContestSearchParamsRef.current = contestSearchParams;
    }
    return changed;
  }, [contestSearchParams]);

  const { items, isError, isLoading, loadMore, hasMore, mutate } =
    useContestSearch(contestSearchParams);

  if (status === "loading") return null;
  if (isError) return null;


  return (
    <ContestList
      mode="published"
      user={undefined}
      isAdmin={isAdmin}
      isLoading={isLoading}
      hasMore={hasMore}
      items={items}
      mutate={mutate}
      loadMore={loadMore}
      q={q}
      onChangeQ={setQ}
      onCommitSearch={commitSearch}
      selectedStatuses={selectedStatuses}
      onChangeStatuses={setSelectedStatuses}
      selectedCategoryIds={selectedCategoryIds}
      onChangeSelectedCategoryIds={setSelectedCategoryIds}
      startFrom={startFrom}
      onChangeStartFrom={setStartFrom}
      startTo={startTo}
      onChangeStartTo={setStartTo}
      adminScope={"ALL" as any}
      onChangeAdminScope={() => { }}
      selectedUserIds={selectedUserIds}
      onChangeSelectedUserIds={setSelectedUserIds}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onChangeSortBy={setSortBy}
      onChangeSortOrder={setSortOrder}
      lockFilter={lockFilter}
      defaultLockFilter="all"
      onChangeLockFilter={setLockFilter}
      shouldResetScroll={shouldResetScroll}
    />
  );
};

export default PublishedContest;